import { useMemo } from 'react'
import { transformPieChartData } from '../utils/transformPieChartData'
import { EventsByClassificationCount } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { Box, Typography } from '@mui/material'
import { theme, Spinner } from '@ketch-com/deck'

interface DataSentrySensitivityChartProps {
  eventsByClassification?: EventsByClassificationCount[]
  isLoading?: boolean
}

export const DataSentrySensitivityChart: React.FC<DataSentrySensitivityChartProps> = ({
  eventsByClassification = [],
  isLoading,
}) => {
  const transformedData = useMemo(
    () => (eventsByClassification?.length ? transformPieChartData(eventsByClassification) : []),
    [eventsByClassification],
  )
  const personalData = transformedData.find(data => data.name === 'Personal')
  const sensitiveData = transformedData.find(data => data.name === 'Sensitive')

  return (
    <Box
      sx={{
        background: theme.palette.Common.Container,
        height: '266px',
        borderRadius: '11px',
        padding: '16px 20px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <Typography variant="h4">Collected Data Sensitivity</Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="flex-end" flex={1}>
          <Box flexBasis={300} alignSelf="stretch" display="flex" justifyContent="space-between" alignItems="flex-end">
            <Box
              flexBasis={130}
              alignSelf="stretch"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexDirection="column"
            >
              <Box
                sx={{
                  backgroundColor: transformedData?.length ? theme.palette.Blue.Default.o100 : theme.palette.Black.o8,
                  width: '100%',
                  minHeight: personalData?.value ? `${personalData.value}%` : '0%',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <Box px={1} py={0.75}>
                  <Typography
                    variant="label"
                    color={transformedData?.length ? theme.palette.White.o100 : theme.palette.Black.o100}
                  >
                    {personalData?.value || 0}%
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="smallBody">{personalData?.name || 'No Data'}</Typography>
              </Box>
            </Box>
            <Box
              flexBasis={130}
              alignSelf="stretch"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexDirection="column"
            >
              <Box
                sx={{
                  backgroundColor: transformedData?.length ? theme.palette.Blue.Light.o100 : theme.palette.Black.o8,
                  width: '100%',
                  minHeight: sensitiveData?.value ? `${sensitiveData.value}%` : '0%',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <Box px={1} py={0.75}>
                  <Typography variant="label">{sensitiveData?.value || 0}%</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="smallBody">{sensitiveData?.name || 'No Data'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
